import { notification } from 'antd';
import { auth } from 'firebase/firebase';
import moment from 'moment';

import { getRange } from '@src/packages/dashboard/utils';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import { translateString } from '@src/util/intl/translateString';

import { DATE_FROM_DEFAULT } from '../../../constants';
import { buildTableParams } from '../../TableCard/constants';
import { createBaseActionButtons } from './baseActionButtons';
import { getChartAccess } from './chartAccess';

/**
 * Creates action buttons for table widgets
 * Includes base buttons with custom download action (CSV) and specific allowedRoles
 * The download action is independent and handles CSV export using the table data
 *
 * @param {Object} params - Parameters for creating action buttons
 * @param {Function} params.handleEdit - Handler for edit action
 * @param {Function} params.handleDelete - Handler for delete action
 * @param {Function} params.setDownloading - Function to set downloading state
 * @param {boolean} params.downloading - Whether download is in progress
 * @param {Object} params.widget - Widget object (contains queryParams, title)
 * @param {Object} params.filters - Filter object (contains dateRange, relative, groupBy)
 * @param {Object} params.additionalProps - Additional props (contains orgId, queryDivisionIds, boardSelected)
 * @returns {Array} Array of action button objects
 */
export const getTableActionButtons = ({
  handleEdit,
  handleDelete,
  setDownloading,
  downloading,
  widget,
  filters,
  additionalProps,
}) => {
  const { orgId, queryDivisionIds, boardSelected } = additionalProps;
  const { formId, triggerIds = [] } = widget.queryParams || {};
  const {
    dateRange,
    relative: { dateType, relativeAmount, isRelative },
    groupBy,
  } = filters;

  // Format date range
  let dateRangeFormated = dateRange || [DATE_FROM_DEFAULT, moment()];
  if (dateRange?.length === 2) {
    dateRangeFormated = [moment(dateRange[0]), moment(dateRange[1])];
  }

  // Build parameters for CSV download
  const dashboardParams = buildTableParams({
    formId,
    uid: auth.currentUser.uid,
    orgId,
    range: getRange(dateRangeFormated, dateType, isRelative, relativeAmount),
    interval: groupBy,
    divisionIds: boardSelected?.divId ? [boardSelected.divId] : queryDivisionIds,
    triggerIds,
    lastPosition: 0,
    pageSize: 20,
    sortFields: [],
  });

  // Independent download handler for CSV export
  const handleDownloadCsv = async () => {
    if (setDownloading) {
      setDownloading(true);
    }

    try {
      const getTableAsCSV = apiCallable(API_OPERATIONS.TABLE_DASHBOARD);
      const { data } = await getTableAsCSV({
        ...dashboardParams,
        saveCSV: true,
      });

      if (!data?.data) throw new Error('No data found');

      const base64Data = data.data;
      const binaryData = atob(base64Data);
      const utf8Data = new TextDecoder('utf-8').decode(
        Uint8Array.from(binaryData, c => c.charCodeAt(0)),
      );
      const bom = '\uFEFF';

      const blob = new Blob([bom + utf8Data], { type: 'text/csv;charset=utf-8;' });

      const url = window.URL.createObjectURL(blob);
      const date = new Date();
      const formattedDate = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${widget.title}-${formattedDate}.csv`;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      notification.error({
        message: translateString('general.save.error.message'),
      });
    } finally {
      if (setDownloading) {
        setDownloading(false);
      }
    }
  };

  // Create base buttons with table-specific allowedRoles and custom CSV download
  return createBaseActionButtons({
    handleEdit,
    handleDelete,
    handleDownload: handleDownloadCsv,
    downloading,
    ...getChartAccess(boardSelected),
    iconSize: 'sm',
  });
};

